import React, { useState } from 'react';
import { Col, Form, Row } from 'antd';
import { LockFilled } from '@ant-design/icons';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import CommonFooter from '@/components/CommonFooter';
import type {
  CredentialData,
  UpdateCredentialPayload,
} from '@/apis/customerCredentials';
import {
  buildUpdatePayload,
  MASKED_SECRET,
  NAME_MAX_LENGTH,
  secretLabel,
} from '../credentialFormat';
import type { EditFormValues } from '../credentialFormat';
import '../../Sensors/sensors.css';
import styles from './CredentialEditForm.module.css';

/** The backend's text for a taken name, shown under the Name field instead of as a toast. Matches
 * `DUPLICATE_NAME_MESSAGE` in customer_credentials/services/operations.py. */
export const DUPLICATE_NAME_MESSAGE =
  'A credential with this name already exists';

const TYPE_LABELS: Record<CredentialData['auth_type'], string> = {
  API_KEY: 'API key',
  OAUTH_CLIENT_CREDENTIALS: 'OAuth (client credentials)',
};

const inputProps = {
  bgColor: 'var(--light-gray)',
  textColor: 'var(--text-primary)',
  height: '36px',
  borderRadius: '8px',
  border: 'none',
} as const;

/** How a save ended, so the form can keep the drawer open and show `message` on the field that caused it. */
export interface SaveOutcome {
  ok: boolean;
  message?: string;
}

interface CredentialEditFormProps {
  credential: CredentialData;
  saving: boolean;
  onSave: (
    payload: UpdateCredentialPayload,
    changedSecret: boolean
  ) => Promise<SaveOutcome>;
  onCancel: () => void;
}

/**
 * The "Edit credentials" drawer (PRD 4.1 Edit). The type is locked, and the
 * secret is write-only: it shows a fixed placeholder with a Replace action
 * that clears the field for a new value. The stored value is never fetched,
 * so it can't be in the DOM; leaving the field alone keeps it.
 */
const CredentialEditForm: React.FC<CredentialEditFormProps> = ({
  credential,
  saving,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm<EditFormValues>();
  const [replacing, setReplacing] = useState(false);
  const isOAuth = credential.auth_type === 'OAUTH_CLIENT_CREDENTIALS';
  const label = secretLabel(credential.auth_type);
  const secretTitle = isOAuth ? 'Client secret' : 'API key';
  const headerLabel =
    credential.api_key_placement === 'QUERY'
      ? 'Sent as query parameter'
      : 'Sent as header';

  const handleFinish = async (values: EditFormValues): Promise<void> => {
    const payload = buildUpdatePayload(credential, values);
    if (Object.keys(payload).length === 0) {
      onCancel();
      return;
    }
    const outcome = await onSave(payload, Boolean(payload.secret));
    if (!outcome.ok && outcome.message === DUPLICATE_NAME_MESSAGE) {
      form.setFields([{ name: 'name', errors: [DUPLICATE_NAME_MESSAGE] }]);
    }
  };

  const stopReplacing = (): void => {
    setReplacing(false);
    form.setFieldValue('secret', undefined);
    form.setFields([{ name: 'secret', errors: [] }]);
  };

  const secretField = replacing ? (
    <Form.Item
      label={secretTitle}
      name='secret'
      rules={[
        {
          required: true,
          whitespace: true,
          message: `Enter the new ${label}, or keep the current one`,
        },
      ]}
      extra={
        <span className={styles.helperText}>
          Values are write-only. Replacing takes effect on next run.
        </span>
      }
    >
      <CustomInput
        type='password'
        autoComplete='new-password'
        placeholder={`Paste the new ${label}`}
        suffix={
          <button
            type='button'
            className={styles.replaceAction}
            onClick={stopReplacing}
          >
            Keep current
          </button>
        }
        {...inputProps}
      />
    </Form.Item>
  ) : (
    <Form.Item
      label={secretTitle}
      extra={
        <span className={styles.helperText}>
          Values are write-only. Replacing takes effect on next run.
        </span>
      }
    >
      <CustomInput
        disabled
        value={MASKED_SECRET}
        aria-label={`${secretTitle} (saved, hidden)`}
        prefix={<LockFilled className={styles.lockIcon} />}
        suffix={
          <button
            type='button'
            className={styles.replaceAction}
            onClick={() => setReplacing(true)}
          >
            Replace
          </button>
        }
        {...inputProps}
      />
    </Form.Item>
  );

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      layout='vertical'
      preserve={false}
      initialValues={{
        name: credential.name,
        api_key_param_name: credential.api_key_param_name ?? '',
        oauth_client_id: credential.oauth_client_id ?? '',
        oauth_token_url: credential.oauth_token_url ?? '',
        oauth_scopes: credential.oauth_scopes ?? '',
      }}
      className='sensor-credential-form'
    >
      <div className='custom-scrollbar sensor-credential-scroll'>
        <Row gutter={[16, 14]}>
          <Col span={24}>
            <CustomTitle
              text='Edit credentials'
              fontSize={24}
              fontWeight='700'
              color='var(--text-primary)'
              marginBottom={0}
            />
          </Col>
          <Col span={24}>
            <Form.Item
              label='Name'
              name='name'
              rules={[
                { required: true, whitespace: true, message: 'Enter a name' },
                {
                  max: NAME_MAX_LENGTH,
                  message: `Name can't be longer than ${NAME_MAX_LENGTH} characters`,
                },
              ]}
            >
              <CustomInput {...inputProps} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label='Type'
              extra={
                <span className={styles.helperText}>
                  Type can&apos;t be changed. Create a new credential instead.
                </span>
              }
            >
              <CustomInput
                disabled
                value={TYPE_LABELS[credential.auth_type]}
                aria-label='Type (locked)'
                prefix={<LockFilled className={styles.lockIcon} />}
                {...inputProps}
              />
            </Form.Item>
          </Col>

          {!isOAuth && (
            <>
              <Col span={24}>{secretField}</Col>
              <Col span={24}>
                <Form.Item
                  label={headerLabel}
                  name='api_key_param_name'
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'Enter a name, e.g. X-API-Key',
                    },
                  ]}
                  extra={
                    <span className={styles.helperText}>
                      e.g. X-API-key or Authorization. Added automatically to
                      every request that uses this credential
                    </span>
                  }
                >
                  <CustomInput {...inputProps} />
                </Form.Item>
              </Col>
            </>
          )}

          {isOAuth && (
            <>
              <Col span={24}>
                <Form.Item
                  label='Client ID'
                  name='oauth_client_id'
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'Enter the client ID',
                    },
                  ]}
                >
                  <CustomInput {...inputProps} />
                </Form.Item>
              </Col>
              <Col span={24}>{secretField}</Col>
              <Col span={24}>
                <Form.Item
                  label='Token URL'
                  name='oauth_token_url'
                  rules={[
                    { required: true, message: 'Enter the token URL' },
                    { type: 'url', message: 'Must be a valid URL' },
                  ]}
                >
                  <CustomInput {...inputProps} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label='Scopes'
                  name='oauth_scopes'
                  extra={
                    <span className={styles.helperText}>
                      Space-separated. Tokens are fetched and refreshed
                      automatically at run time
                    </span>
                  }
                >
                  <CustomInput {...inputProps} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </div>

      <div className='sensor-drawer-footer'>
        <CommonFooter
          text={saving ? 'Saving...' : 'Save'}
          onSave={() => form.submit()}
          handleCancel={onCancel}
        />
      </div>
    </Form>
  );
};

export default CredentialEditForm;
